import { useQuery, useMutation } from "@apollo/client";
import { useState } from "react";
import { ORGANISATION_USERS_QUERY } from "@/graphql/queries";
import {
  INVITE_TEAM_MEMBER_MUTATION,
  ASSIGN_ROLE_MUTATION,
  REMOVE_MEMBER_MUTATION,
} from "@/graphql/mutations";
import { ROLES_QUERY } from "@/graphql/queries";

import SecondaryNavigation from "@/components/navigation/SecondaryNavigation";
import { getNavWithCurrent } from "../../components/utils/settingsNavUtils";
import { useSelector } from "react-redux";
import { RootState } from "@/store/reducers";
import Avatar from "@/components/common/Avatar";
import { TrashIcon } from "@heroicons/react/24/outline";
import AdminGuard from "@/components/guards/AdminGuard";

interface Role {
  id: string;
  name: string;
  description?: string;
}

function TeamMembersComponent() {
  const currentUser = useSelector((state: RootState) => state.user);
  const { data, loading, error, refetch } = useQuery(ORGANISATION_USERS_QUERY);
  const [assignRole, { loading: assigningRole }] = useMutation(
    ASSIGN_ROLE_MUTATION,
    {
      onCompleted: () => refetch(),
    }
  );

  const {
    data: rolesData,
    loading: loadingRoles,
    error: errorRoles,
  } = useQuery(ROLES_QUERY);

  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [inviteTeamMember, { loading: inviting, error: inviteError }] =
    useMutation(INVITE_TEAM_MEMBER_MUTATION, {
      onCompleted: () => {
        setModalOpen(false);
        setEmail("");
        refetch();
      },
    });

  const [removeMember, { loading: removingMember, error: removeError }] = useMutation(REMOVE_MEMBER_MUTATION, {
    onCompleted: () => refetch(),
  });

  const isAdmin = currentUser?.role?.name === "admin";

  const ROLES: Role[] = rolesData?.roles || [];

  const handleRoleChange = async (membershipId: string, newRole: string) => {
    const role: Role | undefined = ROLES.find((r) => r.id === newRole);
    if (!role) return;
    await assignRole({ variables: { membershipId, roleId: newRole } });
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    await inviteTeamMember({ variables: { email } });
  };

  if (loadingRoles)
    return <div className="p-8 text-center">Loading roles…</div>;
  if (errorRoles)
    return (
      <div className="p-8 text-center text-red-500">Error loading roles</div>
    );

  return (
    <main>
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">General Settings</h1>

        <SecondaryNavigation items={getNavWithCurrent("Team members")} />

        <main className="px-4 py-16 sm:px-6 lg:flex-auto lg:px-0 lg:py-10">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
                  Team Members
                </h1>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  A list of all the users in your account including their name,
                  title, email and role.
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <button
                  type="button"
                  className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => setModalOpen(true)}
                >
                  Add Team Member
                </button>
              </div>
            </div>

            <div className="mt-8 flow-root">
              <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Status
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Role
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-600">
                      {loading && (
                        <tr>
                          <td colSpan={5} className="text-center py-8">
                            Loading team members…
                          </td>
                        </tr>
                      )}
                      {error && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center py-8 text-red-500 dark:text-red-400"
                          >
                            Error loading team members{error?.message ? `: ${error.message}` : ""}
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        !error &&
                        data &&
                        data.organisationUsers.totalCount === 0 && (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-center py-8 dark:text-gray-300"
                            >
                              No team members found
                            </td>
                          </tr>
                        )}
                      {!loading &&
                        !error &&
                        data &&
                        data.organisationUsers.nodes.map((person: any) => (
                          <tr key={person.email}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                              <div className="flex items-center gap-x-4">
                                <Avatar
                                  avatarUrl={person.user.avatar}
                                  name={person.user.name}
                                  size={8}
                                />
                                <div className="min-w-0 flex-auto">
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {person.user.name}
                                  </div>
                                  <div className="mt-1 text-gray-500 dark:text-gray-300">
                                    {person.user.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap text-center px-3 py-5 text-sm text-gray-500 dark:text-gray-300">
                              <span className="text-gray-900 dark:text-gray-100">
                                {person.user.title}
                              </span>
                            </td>
                            <td className="whitespace-nowrap text-center px-3 py-5 text-sm text-gray-500 dark:text-gray-300">
                              {person.user.status ? (
                                person.user.status === "active" ||
                                  person.user.active ? (
                                  <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20 dark:ring-green-600/20">
                                    Active
                                  </span>
                                ) : person.user.status === "pending" ? (
                                  <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20 dark:ring-yellow-600/20">
                                    Pending
                                  </span>
                                ) : person.user.status === "deactivated" ? (
                                  <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20 dark:ring-red-600/20">
                                    Suspended
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20 dark:ring-gray-600/20">
                                    {person.user.status
                                      .charAt(0)
                                      .toUpperCase() +
                                      person.user.status.slice(1)}
                                  </span>
                                )
                              ) : (
                                <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20 dark:ring-gray-600/20">
                                  Unknown
                                </span>
                              )}
                            </td>
                            <td className="whitespace-nowrap text-center px-3 py-5 text-sm text-gray-500 dark:text-gray-300">
                              {isAdmin && currentUser.id !== person.user.id ? (
                                ROLES.length > 0 ? (
                                  <select
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm text-gray-900"
                                    value={person.role?.id}
                                    onChange={(e) =>
                                      handleRoleChange(
                                        person.id,
                                        e.target.value
                                      )
                                    }
                                    disabled={assigningRole}
                                  >
                                    {ROLES.map((role) => (
                                      <option key={role.id} value={role.id}>
                                        {role.name.toUpperCase()}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="italic text-gray-400">
                                    No roles available
                                  </span>
                                )
                              ) : (
                                person.role && person.role.name.toUpperCase()
                              )}
                            </td>
                            <td className="whitespace-nowrap text-center px-3 py-5 text-sm text-gray-500 dark:text-gray-300">
                              {isAdmin && currentUser.id !== person.user.id && (
                                <button
                                  type="button"
                                  className="inline-flex items-center justify-center rounded-md bg-red-100 hover:bg-red-200 dark:bg-red-900 dark:hover:bg-red-800 p-2 text-red-600 dark:text-red-300 focus:outline-none focus:ring-2 focus:ring-red-500"
                                  aria-label="Remove"
                                  disabled={removingMember}
                                  onClick={async () => {
                                    if (window.confirm(`Are you sure you want to remove ${person.user.name}?`)) {
                                      try {
                                        await removeMember({ variables: { membershipId: person.id, userId: person.user.id } });
                                      } catch (err) {
                                        // error handled below
                                      }
                                    }
                                  }}
                                >
                                  <TrashIcon className="h-5 w-5" />
                                  <span className="sr-only">Remove</span>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* Modal for inviting team member */}
            {modalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-8 w-full max-w-md relative">
                  <button
                    className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400"
                    onClick={() => setModalOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                  <form onSubmit={handleInvite}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Invite by Email
                    </label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-gray-100"
                      placeholder="email@example.com"
                    />
                    <div className="mt-4 flex justify-end gap-2">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-400"
                        onClick={() => setModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="inline-flex items-center rounded-md bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                        disabled={inviting}
                      >
                        {inviting ? "Inviting..." : "Invite"}
                      </button>
                    </div>
                    {inviteError && (
                      <div className="mt-2 text-red-500 text-center">
                        {inviteError.message}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            )}
          </div>
          {/* Removal error message */}
          {removeError && (
            <div className="p-4 text-center text-red-500">Failed to remove member: {removeError.message}</div>
          )}
        </main>
      </div>
    </main>
  );
}

export default function TeamMembers() {
  return (
    <AdminGuard>
      <TeamMembersComponent />
    </AdminGuard>
  );
}

